import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, BookPlus, Bell, UserPlus, Users, Sparkles, CheckCircle2, Download } from 'lucide-react';
import { adminGetPlatformStats } from '../../utils/remote';
import type { AdminPlatformStats } from '../../types/admin';
import { Badge, Button, Chip, EmptyState, ErrorPanel, Initials, KpiCard, PageHeader, PanelCard, Segmented, Table, Td, TextLink, Th, Track } from '../../school_panel/components/ui';
import { initials, todayEyebrow } from '../../school_panel/pages/dashboards/sections/format';
import { downloadCsv, toCsv } from '../../school_panel/lib/csv';
import GrowthChart from './GrowthChart';
import { RoleBadge, dayLabel, compactCredits, creditsValue } from './adminUi';

const num = (n: number) => n.toLocaleString('en-US');
const usd = (s: string) => {
  const v = Number(s);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '—';
};
const orDash = (s: string | null | undefined) => (s && s.trim() ? s : '—');
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-GB', { month: 'short' });
};

type Metric = 'students' | 'active' | 'exams';
const METRIC_LABEL: Record<Metric, string> = { students: 'Students', active: 'Active', exams: 'Exams' };
const METRIC_NOTE: Record<Metric, string> = { students: 'Enrolled students', active: 'Active students per month', exams: 'Exams taken per month' };

// One severity tag per school (design `.att.hi` / `.att.md`): a quiet school is High, the rest Med.
const isHigh = (items: AdminPlatformStats['attention']) => items.some((a) => a.reason === 'no_activity_14d');

/** 2,500,000 → "2.5M"; 150,000 → "150k". */
const compactCount = compactCredits;

const ACTIONS = [
  { to: '/admin/rag-setup', title: 'Add a textbook source', body: 'Upload, map, index for RAG', Icon: BookPlus, hot: true },
  { to: '/admin/notification', title: 'Send a push notification', body: 'To all, a school or a grade', Icon: Bell },
  { to: '/admin/users', title: 'Invite a user', body: 'Admin, teacher or superuser', Icon: UserPlus },
  { to: '/admin/school-ai-usage', title: 'Review AI credits', body: 'Credits per school user', Icon: Sparkles },
];

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading platform stats" className="animate-pulse">
      <div className="grid grid-cols-2 gap-[18px] min-[1180px]:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-[128px] rounded-[20px] bg-track" />)}
      </div>
      <div className="mt-[18px] grid grid-cols-1 gap-[18px] min-[1180px]:grid-cols-12">
        <div className="h-[300px] rounded-[20px] bg-track min-[1180px]:col-span-8" />
        <div className="h-[300px] rounded-[22px] bg-track min-[1180px]:col-span-4" />
      </div>
    </div>
  );
}

function examsDelta(r: AdminPlatformStats['results']) {
  if (r.prev_30d <= 0) return undefined;
  const p = Math.round(((r.last_30d - r.prev_30d) / r.prev_30d) * 100);
  if (p === 0) return <Chip tone="neutral">0%</Chip>;
  return <Chip tone={p > 0 ? 'up' : 'down'}>{p > 0 ? '↑' : '↓'} {Math.abs(p)}%</Chip>;
}

function typeNote(t: AdminPlatformStats['institutions']['by_type']) {
  const parts: string[] = [];
  if (t.school) parts.push(`${t.school} ${t.school === 1 ? 'school' : 'schools'}`);
  if (t.college) parts.push(`${t.college} ${t.college === 1 ? 'college' : 'colleges'}`);
  if (t.university) parts.push(`${t.university} ${t.university === 1 ? 'university' : 'universities'}`);
  return parts.length ? parts.join(' · ') : 'No institutions yet';
}

type AttentionSchool = { institution_id: number; name: string; items: AdminPlatformStats['attention'] };

// One row per school: the API lists one item per (school, reason).
function groupAttention(items: AdminPlatformStats['attention']): AttentionSchool[] {
  const by = new Map<number, AttentionSchool>();
  items.forEach((a) => {
    const g = by.get(a.institution_id);
    if (g) g.items.push(a);
    else by.set(a.institution_id, { institution_id: a.institution_id, name: a.name, items: [a] });
  });
  return Array.from(by.values());
}

const schoolsWord = (n: number) => (n === 1 ? 'school' : 'schools');

function exportReport(s: AdminPlatformStats) {
  const rows: (string | number)[][] = [
    ['Schools', s.institutions.total], ['Students', s.students.total], ['Students joined (30 days)', s.students.joined_30d],
    ['Students active (7 days)', s.students.active_7d], ['Exams taken (30 days)', s.results.last_30d], ['Exams taken (prior 30 days)', s.results.prev_30d],
    ['AI credits used this month', s.ai.credits_used_month ?? ''], ['AI credits allocated', s.ai.credits_allocation_total ?? ''],
    ...s.growth.map((g) => [`Growth ${g.month}: students joined / exams taken`, `${g.students_joined} / ${g.results}`]),
    ...s.most_active_schools.map((m) => [`Most active: ${m.name} (active / students)`, `${m.active_7d} / ${m.students}`]),
  ];
  downloadCsv('yada-platform-overview.csv', toCsv(['Measure', 'Value'], rows));
}

function Dashboard({ s }: { s: AdminPlatformStats }) {
  const attn = groupAttention(s.attention);
  const attnTotal = Math.max(s.attention_total ?? 0, attn.length);
  const hasActive = s.growth.length > 0 && s.growth.every((g) => typeof g.active === 'number');
  const metrics = (['students', ...(hasActive ? ['active'] : []), 'exams'] as Metric[]).map((k) => ({ key: k, label: METRIC_LABEL[k] }));
  const [metric, setMetric] = useState<Metric>('students');
  const activePct = s.students.total ? (s.students.active_7d / s.students.total) * 100 : 0;
  // Enrolled students per month: today's total minus everyone who joined after that month.
  const series = s.growth.map((g, i) => {
    if (metric === 'active') return g.active ?? 0;
    if (metric === 'exams') return g.results;
    return s.students.total - s.growth.slice(i + 1).reduce((a, x) => a + x.students_joined, 0);
  });
  const points = s.growth.map((g, i) => ({
    label: monthLabel(g.month), value: series[i], title: `${monthLabel(g.month)} ${g.month.slice(0, 4)}: ${num(series[i])}`,
  }));
  const trend = series.length > 1 ? (series[series.length - 1] > series[0] ? 'rose' : series[series.length - 1] < series[0] ? 'fell' : 'held steady') : 'was';
  const ariaLabel = series.length > 1
    ? `${METRIC_NOTE[metric]} ${trend} from ${num(series[0])} to ${num(series[series.length - 1])} over ${series.length} months`
    : `${METRIC_NOTE[metric]}: ${num(series[0] ?? 0)}`;

  const used = s.ai.credits_used_month;
  const alloc = s.ai.credits_allocation_total;
  const hasCredits = typeof used === 'number' && typeof alloc === 'number' && alloc > 0;
  const creditPct = hasCredits ? Math.round((used / alloc) * 100) : 0;
  const inst = s.institutions;
  const schoolsActive = typeof inst.active_window === 'number' ? inst.active_window : null;
  return (
    <>
      <div className="mb-[18px] grid grid-cols-2 gap-[18px] min-[641px]:grid-cols-3 min-[1181px]:grid-cols-5">
        <KpiCard label="Schools" value={num(inst.total)}
          chip={inst.new_30d ? <Chip tone="up">+{num(inst.new_30d)} new</Chip> : undefined}
          track={schoolsActive !== null && inst.total > 0 ? <Track value={(schoolsActive / inst.total) * 100} label="Schools with student activity in the last 14 days" /> : undefined}
          sub={schoolsActive !== null ? `Active on platform: ${num(schoolsActive)}` : typeNote(inst.by_type)} />
        <KpiCard label="Students" value={num(s.students.total)}
          chip={s.students.joined_30d > 0 ? <Chip tone="up">+{num(s.students.joined_30d)}</Chip> : undefined}
          track={s.students.total > 0 ? <Track value={100} label="All enrolled students" /> : undefined}
          sub="Joined in the last 30 days" />
        <KpiCard label="Active" value={s.students.total > 0 ? num(s.students.active_7d) : '—'}
          chip={s.students.total > 0 ? <Chip tone="up">{Math.round(activePct)}%</Chip> : undefined}
          track={s.students.total > 0 ? <Track value={activePct} label="Students who took an exam in the last 7 days" /> : undefined}
          sub={`${num(s.students.active_7d)} of ${num(s.students.total)} took an exam in 7 days`} />
        <KpiCard label="Exams taken" chip={examsDelta(s.results)} value={num(s.results.last_30d)}
          sub={s.results.prev_30d > 0 ? `last 30 days · ${num(s.results.prev_30d)} before` : 'last 30 days'} />
        {hasCredits ? (
          <KpiCard label="AI credits" value={creditsValue(used).value} unit={creditsValue(used).unit}
            chip={<Chip tone={creditPct > 70 ? 'down' : 'up'}>{creditPct}%</Chip>}
            track={<Track value={creditPct} tone={creditPct > 70 ? 'accent' : 'slate'} label="Share of this month's AI credit allocation used" />}
            sub={`of ${compactCount(alloc)} monthly allocation`} />
        ) : (
          <KpiCard label="AI spend" value={usd(s.ai.total_spent_usd)} sub={`this month ${usd(s.ai.monthly_spent_usd)}`} />
        )}
      </div>

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-12">
        <PanelCard className="min-[821px]:col-span-6 min-[1181px]:col-span-8" title="Platform growth" subtitle={`${METRIC_NOTE[metric]} · last ${s.growth.length} months`}
          actions={<Segmented label="Growth metric" options={metrics} value={metric} onChange={setMetric} />}>
          {s.growth.length === 0 ? (
            <EmptyState compact icon={<CheckCircle2 size={20} />} title="No growth data yet" />
          ) : (
            <GrowthChart points={points} ariaLabel={ariaLabel} />
          )}
        </PanelCard>

        <section aria-label="Needs attention" className="flex min-w-0 flex-col gap-4 rounded-[22px] bg-navy p-6 text-on-navy min-[821px]:col-span-6 min-[1181px]:col-span-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Needs attention</div>
              {attn.length > 0 && (
                <div className="mt-2.5 flex items-baseline gap-2.5">
                  <span className="font-mono text-[52px] font-semibold leading-none tracking-[-0.04em]">{attnTotal}</span>
                  <span className="font-display text-lg font-bold">{schoolsWord(attnTotal)}</span>
                </div>
              )}
            </div>
            {attn.length > 0 && inst.total > 0 && (
              <span className="flex-none rounded-full bg-navy-2 px-2 py-[3px] font-mono text-xs font-semibold text-on-navy">of {num(inst.total)}</span>
            )}
          </div>
          {attn.length === 0 ? (
            <div className="flex flex-col items-center px-4 py-4 text-center">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-navy-2 text-on-navy"><CheckCircle2 size={20} aria-hidden /></span>
              <h4 className="mt-2 font-display text-sm font-bold text-on-navy">Nothing needs attention</h4>
              <p className="max-w-sm text-xs text-on-navy-muted">No school needs attention right now.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {attn.map((g) => (
                <li key={g.institution_id}>
                  <Link to="/admin/schools" aria-label={`Review ${g.name}`}
                    className="flex items-center gap-3 rounded-[14px] bg-navy-2 p-3 text-on-navy no-underline transition-colors hover:bg-navy-3 hover:text-on-navy">
                  <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] bg-mist-fixed text-xs font-semibold text-navy">{initials(g.name)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{g.name}</span>
                    <span className="block truncate text-[13px] text-on-navy-muted">{g.items.map((a) => a.detail).join(' · ')}</span>
                  </span>
                  <span className={`flex-none whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-semibold ${isHigh(g.items) ? 'bg-tag-bg text-tag-text' : 'bg-tag-warn-bg text-tag-warn-text'}`}>
                    {isHigh(g.items) ? 'High' : 'Med'}
                  </span>
                  </Link>
                </li>
              ))}
              {attnTotal > attn.length && (
                <li className="px-1 text-[13px] text-on-navy-muted">+{attnTotal - attn.length} more {schoolsWord(attnTotal - attn.length)} flagged</li>
              )}
            </ul>
          )}
          {attn.length > 0 && (
            <Link to="/admin/schools"
              className="mt-auto inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy no-underline transition hover:brightness-105 hover:text-navy">
              Review schools <ArrowRight size={16} strokeWidth={1.8} aria-hidden />
            </Link>
          )}
        </section>
      </div>

      <nav aria-label="Quick actions" className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-4">
        {ACTIONS.map(({ to, title, body, Icon, hot }) => (
          <Link key={to} to={to}
            className="flex min-h-[76px] items-center gap-3.5 rounded-[18px] border border-line bg-surface p-4 text-ink no-underline transition hover:-translate-y-0.5 hover:text-ink hover:shadow-card-hover">
            <span className={`grid h-11 w-11 flex-none place-items-center rounded-[14px] ${hot ? 'bg-accent text-navy' : 'bg-mist text-slate'}`}>
              <Icon size={22} strokeWidth={1.8} aria-hidden />
            </span>
            <span className="flex-1">
              <span className="block font-semibold">{title}</span>
              <span className="text-[13px] text-muted">
                {to === '/admin/school-ai-usage' && typeof s.ai.schools_over_90 === 'number'
                  ? (s.ai.schools_over_90 > 0 ? `${s.ai.schools_over_90} ${schoolsWord(s.ai.schools_over_90)} above 90%` : 'No schools above 90%')
                  : body}
              </span>
            </span>
          </Link>
        ))}
      </nav>

      <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-12">
        <PanelCard className="min-[821px]:col-span-6 min-[1181px]:col-span-7" title="Recent signups"
          actions={<TextLink to="/admin/users">All users →</TextLink>}>
          {s.recent_signups.length === 0 ? (
            <EmptyState compact icon={<Users size={20} />} title="No signups yet" />
          ) : (
            <Table minWidth={560}>
              <thead>
                <tr>
                  <Th>User</Th><Th>Role</Th><Th>School</Th><Th>Joined</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {s.recent_signups.slice(0, 6).map((u) => (
                  <tr key={u.id}>
                    <Td>
                      <span className="flex min-w-0 items-center gap-3">
                        <Initials name={u.name.trim() || u.username} />
                        <span className="min-w-0">
                          <span className="block font-semibold text-ink">{u.name.trim() || u.username}</span>
                          {u.name.trim() && <span className="block font-mono text-xs font-medium text-muted">@{u.username}</span>}
                        </span>
                      </span>
                    </Td>
                    <Td><RoleBadge role={u.role} isSuperuser={u.is_superuser} /></Td>
                    <Td className="text-ink">{orDash(u.institution_name)}</Td>
                    <Td className="whitespace-nowrap font-mono font-medium text-muted">{dayLabel(u.date_joined)}</Td>
                    <Td><Badge tone={u.is_active ? 'success' : 'warning'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </PanelCard>

        <PanelCard className="min-[821px]:col-span-6 min-[1181px]:col-span-5" title="Most active schools" subtitle="Students active this week"
          actions={<TextLink to="/admin/schools">All →</TextLink>}>
          {s.most_active_schools.length === 0 ? (
            <EmptyState compact icon={<Users size={20} />} title="No activity yet" />
          ) : (
            <ul className="flex flex-col gap-[15px]">
              {s.most_active_schools.map((m) => {
                const p = m.students ? (m.active_7d / m.students) * 100 : 0;
                return (
                  <li key={m.id}>
                    <div className="mb-[7px] flex justify-between gap-2.5">
                      <span className="min-w-0 font-medium text-ink">{m.name} <span className="text-xs text-muted">· {num(m.students)} students</span></span>
                      <span className="flex-none font-mono font-medium">{Math.round(p)}%</span>
                    </div>
                    <Track value={p} tone={p < 50 ? 'accent' : 'slate'} label={`${m.name}: students who took an exam in the last 7 days`} />
                  </li>
                );
              })}
            </ul>
          )}
        </PanelCard>
      </div>
    </>
  );
}

const AdminDashboard: React.FC = () => {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['admin-platform-stats'], queryFn: adminGetPlatformStats });
  const flagged = data ? Math.max(data.attention_total ?? 0, groupAttention(data.attention).length) : 0;
  const subtitle = data
    ? `${num(data.institutions.total)} ${schoolsWord(data.institutions.total)} and ${num(data.students.total)} students on YADA.${flagged > 0 ? ` ${flagged} ${flagged === 1 ? 'school needs' : 'schools need'} a look today.` : ''}`
    : undefined;
  return (
    <div>
      <PageHeader eyebrow={`Platform admin · ${todayEyebrow()}`} title="Platform overview" subtitle={subtitle}
        actions={data ? <Button variant="ghost" icon={<Download size={16} aria-hidden />} onClick={() => exportReport(data)}>Export report</Button> : undefined} />
      {isLoading ? <Skeleton /> : isError || !data ? (
        <PanelCard><ErrorPanel title="Failed to load" body="The platform stats could not be loaded." onRetry={() => refetch()} /></PanelCard>
      ) : <Dashboard s={data} />}
    </div>
  );
};

export default AdminDashboard;
